import React from 'react';
import styled from 'styled-components';

import { Modal, Spinner, Button, Text } from '@companycam/slab-web';

import { translate } from 'javascripts/translations/i18n';
import { useDeleteReviewProfile } from 'data/v3/reviewProfiles';
import {
  useReviewProfiles,
  type ReviewProfile,
} from './hooks/useReviewProfiles.ts';
import { useModal } from 'components/shared/hooks';
import toast from 'helpers/toast';
import { Steven } from 'helpers/steven';
import { useGetReviewRequestCount } from 'data/v3/reviewProfiles/hooks/useGetReviewRequestCount';

export type DeleteReviewProfileModalProps = {
  reviewProfileId: number;
};

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledModal = styled(Modal)``;

const StyledText = styled(Text)`
  margin: var(--cc_size_spacing_m) 0;
`;

const BulletedList = styled.ul`
  list-style-type: disc !important;
  font-size: var(--cc_size_text_s);
  padding-inline-start: var(--cc_size_spacing_l);
  display: grid;
  gap: var(--cc_size_spacing_s);
`;

const DeleteReviewProfileModal = ({
  reviewProfileId,
}: DeleteReviewProfileModalProps) => {
  const { reviewProfiles, isReviewProfilesLoading, refetchReviewProfiles } =
    useReviewProfiles();
  const selectedReviewProfile: ReviewProfile = reviewProfiles.find(
    (reviewProfile) => reviewProfile.id === reviewProfileId,
  );
  const {
    data: reviewRequestCountResponse,
    isLoading: isReviewRequestCountLoading,
    error: errorReviewRequestCount,
  } = useGetReviewRequestCount({
    reviewProfileId,
  });
  const profileNotFound = !selectedReviewProfile && !isReviewProfilesLoading;
  const hideReviewRequestCountWarning =
    !reviewRequestCountResponse?.data?.reviewRequestCount ||
    isReviewRequestCountLoading ||
    errorReviewRequestCount;
  let name = 'Unnamed Location';

  if (selectedReviewProfile?.nickname) {
    name = selectedReviewProfile.nickname;
  }

  const { hideModal } = useModal();

  const handleError = () => {
    hideModal();
    toast.error(
      translate('project.reviews.reviewsEdit.publicProfileDeletedError'),
    );
  };

  const handleSuccess = () => {
    refetchReviewProfiles();
    toast.success(
      translate('project.reviews.reviewsEdit.publicProfileDeletedSucessfully'),
    );
    Steven.entityCrud({
      entity_type: 'review_profile',
      entity_id: reviewProfileId.toString(),
      action: 'deleted',
    });

    hideModal();
  };

  const deleteReviewProfile = useDeleteReviewProfile({
    onError: handleError,
    onSuccess: handleSuccess,
  });

  const handlePublicProfileDelete = () => {
    deleteReviewProfile.mutate({
      reviewProfileId,
    });
  };

  if (isReviewProfilesLoading) {
    return (
      <SpinnerContainer>
        <Spinner
          accessibilityLabel={translate('common.loading')}
          size="large"
        />
      </SpinnerContainer>
    );
  }

  if (profileNotFound) {
    hideModal();
    toast.error(translate('project.reviews.reviewsEdit.profileNotFound'));
  }

  return (
    <StyledModal
      title={null}
      headerComponent={null}
      size="small"
      isOpen
      onClose={hideModal}
      footerActions={
        <>
          <Button onClick={hideModal}>{translate('common.cancel')}</Button>
          <Button
            color="destroy"
            onClick={handlePublicProfileDelete}
            data-testid="reviews__remove-business-modal-Delete"
          >
            {translate('common.delete')}
          </Button>
        </>
      }
    >
      <Text
        as="h2"
        size="xl"
        ccMargin="none none m none"
        data-testid="reviews__remove-business-modal-header"
      >
        {`${translate('common.remove')} ${name}`}
      </Text>
      <StyledText as="p">
        {translate('project.reviews.reviewsEdit.deletePublicProfileMessage', {
          locationName: name,
        })}
      </StyledText>
      <BulletedList>
        <Text as="li" size="m" color="subtle" style={{ display: 'list-item' }}>
          {translate(
            'project.reviews.reviewsEdit.deletePublicProfileHighlightOne',
            {
              locationName: name,
            },
          )}
        </Text>
        {!hideReviewRequestCountWarning ? (
          <Text
            as="li"
            size="m"
            color="subtle"
            style={{ display: 'list-item' }}
          >
            {translate(
              'project.reviews.reviewsEdit.deletePublicProfileHighlightTwo',
              {
                count: reviewRequestCountResponse.data.reviewRequestCount,
              },
            )}
          </Text>
        ) : null}
      </BulletedList>
    </StyledModal>
  );
};

export default DeleteReviewProfileModal;
